import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { fetchMcqsBySlug } from "../../services/api";
import { formatSlug } from "../../utils/slugify";
import RelatedBlogs from "../../components/Blogs/RelatedBlogs/RelatedBlogs";
import Ads from "../../components/Ads/Ads";
import "../AllBlogs/BlogShared.css";
import "./McqsPost.css";

interface McqQuestion {
  _id: string;
  question: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
  answer: "A" | "B" | "C" | "D";
  answer_desc: string;
}

const OPTION_KEYS = ["A", "B", "C", "D"] as const;

const getOptionText = (mcq: McqQuestion, key: string) => {
  switch (key) {
    case "A": return mcq.option_a;
    case "B": return mcq.option_b;
    case "C": return mcq.option_c;
    case "D": return mcq.option_d;
    default:  return "";
  }
};

const McqsPost: React.FC = () => {
  const { slug } = useParams();
  const [mcqs,            setMcqs]            = useState<McqQuestion[]>([]);
  const [loading,         setLoading]         = useState(true);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, string>>({});
  const [showAnswers,     setShowAnswers]     = useState<Record<string, boolean>>({});
  const [showAll,         setShowAll]         = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    fetchMcqsBySlug(slug)
      .then(res => setMcqs(res.data))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [slug]);

  const handleSelect = (qId: string, key: string) => {
    setSelectedAnswers(prev => ({ ...prev, [qId]: key }));
  };

  const toggleAnswer = (qId: string) => {
    setShowAnswers(prev => ({ ...prev, [qId]: !prev[qId] }));
  };

  const toggleAllAnswers = () => {
    if (showAll) {
      setShowAnswers({});
    } else {
      const all: Record<string, boolean> = {};
      mcqs.forEach(m => { all[m._id] = true; });
      setShowAnswers(all);
    }
    setShowAll(p => !p);
  };

  /* Progress stats */
  const attempted  = Object.keys(selectedAnswers).filter(id => mcqs.some(m => m._id === id)).length;
  const correct    = mcqs.filter(m => selectedAnswers[m._id] === m.answer).length;
  const incorrect  = attempted - correct;
  const progressPct = mcqs.length > 0 ? Math.round((attempted / mcqs.length) * 100) : 0;

  return (
    <>
      <Helmet>
        <title>{formatSlug(slug || "")} — MCQs | Acadebility</title>
        <meta name="description" content={`Practice ${formatSlug(slug || "")} MCQs on Acadebility`} />
      </Helmet>

      {/* ── Hero ── */}
      <div className="bl-hero">
        <div className="bl-hero-inner">
          <nav className="bl-breadcrumb">
            <Link to="/">Home</Link><span>/</span>
            <Link to="/blogs/all-blogs/mcqs">All MCQs</Link><span>/</span>
            <span className="bl-breadcrumb-current">{formatSlug(slug || "")}</span>
          </nav>
          <h1 className="bl-hero-title">{formatSlug(slug || "")}</h1>
          <p className="bl-hero-sub">Practice, attempt and verify your answers below</p>
          <div className="bl-hero-pills">
            <div className="bl-hero-pill">🎯 {mcqs.length} Questions</div>
            <div className="bl-hero-pill">✅ {correct} Correct</div>
            <div className="bl-hero-pill">⏱ Self-paced</div>
          </div>
        </div>
        <div className="bl-hero-blob bl-hero-blob-1" />
        <div className="bl-hero-blob bl-hero-blob-2" />
      </div>

      {/* ── Page body ── */}
      <div className="bl-page">
        <div className="bl-layout">

          {/* ── Main ── */}
          <main className="bl-main">

            {/* Progress tracker */}
            {mcqs.length > 0 && (
              <div className="mq-tracker bl-card">
                <div className="mq-tracker-stats">
                  <div className="mq-stat mq-stat-total">
                    <div className="mq-stat-val">{mcqs.length}</div>
                    <div className="mq-stat-label">Total</div>
                  </div>
                  <div className="mq-stat mq-stat-done">
                    <div className="mq-stat-val">{attempted}</div>
                    <div className="mq-stat-label">Attempted</div>
                  </div>
                  <div className="mq-stat mq-stat-correct">
                    <div className="mq-stat-val">{correct}</div>
                    <div className="mq-stat-label">Correct</div>
                  </div>
                  <div className="mq-stat mq-stat-wrong">
                    <div className="mq-stat-val">{incorrect}</div>
                    <div className="mq-stat-label">Incorrect</div>
                  </div>
                  <button
                    className={`mq-reveal-all-btn${showAll ? " mq-reveal-all-active" : ""}`}
                    onClick={toggleAllAnswers}
                    type="button"
                  >
                    {showAll ? "Hide All Answers" : "Reveal All Answers"}
                  </button>
                </div>
                {/* Progress bar */}
                <div className="mq-progress-wrap">
                  <div className="mq-progress-track">
                    <div className="mq-progress-fill" style={{ width: `${progressPct}%` }} />
                  </div>
                  <span className="mq-progress-pct">{progressPct}% attempted</span>
                </div>
              </div>
            )}

            {/* Questions */}
            {loading ? (
              <div className="mq-loading">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div className="mq-skeleton-card" key={i}>
                    <div className="mq-sk-q" />
                    <div className="mq-sk-opts">
                      {[1,2,3,4].map(j => <div className="mq-sk-opt" key={j} />)}
                    </div>
                  </div>
                ))}
              </div>
            ) : mcqs.length === 0 ? (
              <div className="mq-empty bl-card">
                <span>🔍</span>
                <h3>No questions found</h3>
                <p>This MCQ set may be empty or still being prepared.</p>
              </div>
            ) : (
              <div className="mq-list">
                {mcqs.map((mcq, index) => {
                  const selected = selectedAnswers[mcq._id];
                  const answered = !!selected;
                  const isCorrect = selected === mcq.answer;
                  const showAns  = showAnswers[mcq._id];

                  return (
                    <div key={mcq._id} className={`mq-card bl-card${answered ? (isCorrect ? " mq-card-correct" : " mq-card-wrong") : ""}`}>
                      {/* Question number badge */}
                      <div className="mq-card-head">
                        <span className="mq-q-num">Q{index + 1}</span>
                        {answered && (
                          <span className={`mq-feedback-badge${isCorrect ? " mq-badge-correct" : " mq-badge-wrong"}`}>
                            {isCorrect ? "✅ Correct!" : "❌ Wrong"}
                          </span>
                        )}
                      </div>

                      {/* Question text */}
                      <div
                        className="mq-question"
                        dangerouslySetInnerHTML={{ __html: `${index + 1}. ${mcq.question}` }}
                      />

                      {/* Options */}
                      <div className="mq-options">
                        {OPTION_KEYS.map(key => {
                          const isSelected  = selected === key;
                          const isAnswer    = key === mcq.answer;
                          let optionClass   = "mq-option";
                          if (answered) {
                            if (isAnswer)                  optionClass += " mq-option-answer";
                            else if (isSelected && !isAnswer) optionClass += " mq-option-wrong";
                          } else if (isSelected) {
                            optionClass += " mq-option-selected";
                          }

                          return (
                            <button
                              key={key}
                              type="button"
                              className={optionClass}
                              onClick={() => handleSelect(mcq._id, key)}
                              disabled={answered}
                            >
                              <span className="mq-option-key">{key}</span>
                              <span className="mq-option-text">{getOptionText(mcq, key)}</span>
                              {answered && isAnswer && (
                                <span className="mq-option-tick">✓</span>
                              )}
                              {answered && isSelected && !isAnswer && (
                                <span className="mq-option-cross">✗</span>
                              )}
                            </button>
                          );
                        })}
                      </div>

                      {/* View answer button + explanation */}
                      <div className="mq-card-footer">
                        <button
                          className={`mq-answer-btn${showAns ? " mq-answer-btn-hide" : ""}`}
                          onClick={() => toggleAnswer(mcq._id)}
                          type="button"
                        >
                          {showAns ? (
                            <><span>🙈</span> Hide Answer</>
                          ) : (
                            <><span>💡</span> View Answer</>
                          )}
                        </button>
                      </div>

                      {/* Answer explanation */}
                      {showAns && (
                        <div className="mq-explanation">
                          <div className="mq-explanation-head">
                            <span className="mq-explanation-icon">💡</span>
                            <strong>Answer: {getOptionText(mcq, mcq.answer)}</strong>
                          </div>
                          {mcq.answer_desc && (
                            <div
                              className="mq-explanation-body"
                              dangerouslySetInnerHTML={{ __html: mcq.answer_desc }}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </main>

          {/* ── Sidebar ── */}
          <aside className="bl-sidebar">
            <Ads slot="8537390283" />
            <RelatedBlogs />
          </aside>

        </div>
      </div>
    </>
  );
};

export default McqsPost;
